import { useMemo, type ReactNode } from "react";
import { useDroppable } from "@dnd-kit/core";
import {
  SortableContext,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";

import { cardDraggableId, columnDroppableId, type BoardColumn } from "./ids";

interface Props<T extends { contact_id: string }> {
  column: BoardColumn;
  cards: T[];
  isOver: boolean;
  renderCard: (card: T) => ReactNode;
}

export function DroppableColumn<T extends { contact_id: string }>({
  column,
  cards,
  isOver,
  renderCard,
}: Props<T>) {
  const ids = useMemo(
    () => cards.map((c) => cardDraggableId(column.key, c.contact_id)),
    [cards, column.key],
  );
  const { setNodeRef } = useDroppable({ id: columnDroppableId(column.key) });
  return (
    <section
      ref={setNodeRef}
      className={`column ${isOver ? "is-over" : ""}`}
    >
      <header className="column-header">
        <div className="min-w-0">
          <div className="label">{column.label}</div>
          <div className="title truncate">{column.title}</div>
          {column.subtitle ? (
            <div className="text-xs text-ink-low truncate mt-0.5">
              {column.subtitle}
            </div>
          ) : null}
          {column.description ? (
            <div
              className="text-xs text-ink-medium mt-1"
              style={{
                display: "-webkit-box",
                WebkitLineClamp: 2,
                WebkitBoxOrient: "vertical",
                overflow: "hidden",
              }}
              title={column.description}
            >
              {column.description}
            </div>
          ) : null}
        </div>
        <span className="count tnum">{cards.length}</span>
      </header>
      <SortableContext items={ids} strategy={verticalListSortingStrategy}>
        <div className="column-body">
          {cards.length === 0 ? (
            <div className="column-empty">— empty —</div>
          ) : (
            cards.map((card) => renderCard(card))
          )}
        </div>
      </SortableContext>
    </section>
  );
}
